import { ArrowRight, HelpCircle } from 'lucide-react';
import { Component, useId } from 'react';

import { AnimatedSpateAiIcon } from '@/ui/design-system/components/ui/animated-spate-ai-icon';
import {
    Popover,
    PopoverContent,
    PopoverTrigger,
} from '@/ui/design-system/components/ui/popover';
import { SpateAiIcon } from '@/ui/design-system/components/ui/spate-ai-icon';
import { mixpanelTrack } from 'utils/app/configureMixpanel';

import { useAssistantContext } from '../AssistantProvider';
import { useEntityInsight } from './useEntityInsight.hook';

const SKELETON_CARD_COUNT = 4;

// Per-card error boundary so a formatting issue on one insight (bad metric
// string, missing field, etc.) doesn't break the whole bar. The failing card
// renders nothing; siblings render normally. Logged to console so we still
// see the failure.
class InsightCardErrorBoundary extends Component {
    constructor(props) {
        super(props);
        this.state = { hasError: false };
    }
    static getDerivedStateFromError() {
        return { hasError: true };
    }
    componentDidCatch(error, info) {
        // eslint-disable-next-line no-console
        console.error('[EntityInsightBar] card render error', error, info);
    }
    render() {
        if (this.state.hasError) return null;
        return this.props.children;
    }
}

const ShimmerBar = ({ className = '' }) => (
    <div className={`shimmer-bar rounded ${className}`} />
);

// Split a metric string like "+250.1K (+122.0% YoY)" into its absolute and
// percent parts so the card can render them on opposite ends of the metric
// row (absolute prominent, percent muted). Falls back to all-absolute when
// the metric has no parenthetical (rare — happens when the rebuilder
// couldn't compute an aggregate %).
const parseMetric = (metric) => {
    if (typeof metric !== 'string') return { absolute: '', percent: '' };
    const match = metric.match(/^(.+?)\s*\((.+?)\)\s*$/);
    if (match) {
        return { absolute: match[1].trim(), percent: match[2].trim() };
    }
    return { absolute: metric.trim(), percent: '' };
};

// Absolute-number color on hover only — green for growth, red for decline.
// Percent stays muted on hover so the two halves of the metric have clear
// visual hierarchy. Same pattern as the health-stats section above the
// time series.
const absoluteHoverClass = (absolute) => {
    if (typeof absolute !== 'string') return '';
    const trimmed = absolute.trim();
    if (trimmed.startsWith('+')) return 'group-hover:text-green-600';
    if (trimmed.startsWith('-') || trimmed.startsWith('−'))
        return 'group-hover:text-red-600';
    return '';
};

const InsightCardSkeleton = () => (
    <div className="bg-card border border-border rounded-lg flex flex-col gap-1.5 px-3.5 py-2.5">
        <div className="flex items-center justify-between">
            <ShimmerBar className="h-3 w-24" />
            <ArrowRight size={14} className="text-muted-foreground/40" />
        </div>
        <ShimmerBar className="h-4 w-24" />
        <div className="flex items-baseline justify-between gap-2">
            <ShimmerBar className="h-4 w-16" />
            <ShimmerBar className="h-3 w-20" />
        </div>
        <ShimmerBar className="h-3 w-28" />
    </div>
);

const InsightCard = ({ insight, onClick, describedBy }) => {
    const ctaId = useId();
    const { absolute, percent } = parseMetric(insight.metric);
    return (
        <button
            type="button"
            onClick={onClick}
            aria-describedby={`${describedBy} ${ctaId}`}
            className="group bg-card border border-border rounded-lg text-left flex flex-col gap-1.5 px-3.5 py-2.5 hover:border-primary/40 hover:shadow-sm transition cursor-pointer focus-visible:outline-none focus-visible:ring-[3px] focus-visible:ring-ring"
        >
            <div className="flex items-center justify-between text-muted-foreground group-hover:text-primary transition-colors">
                <span className="text-xs font-medium lowercase first-letter:uppercase">
                    {insight.label}
                </span>
                <ArrowRight size={14} />
            </div>
            <span className="text-sm font-medium text-foreground capitalize">
                {insight.value}
            </span>
            {insight.metric ? (
                <div className="flex items-baseline justify-between gap-2">
                    <span
                        className={`text-sm font-semibold text-foreground transition-colors ${absoluteHoverClass(absolute)}`}
                    >
                        {absolute}
                    </span>
                    {percent && (
                        <span className="text-xs text-muted-foreground whitespace-nowrap">
                            {percent}
                        </span>
                    )}
                </div>
            ) : (
                // Metric not yet finalised — server rebuilder hasn't returned
                // the canonical value. Two shimmer bars matching the split
                // layout so the loaded state doesn't visually jump.
                <div className="flex items-baseline justify-between gap-2">
                    <ShimmerBar className="h-4 w-16" />
                    <ShimmerBar className="h-3 w-20" />
                </div>
            )}
            {insight.sub && (
                <span className="text-xs text-muted-foreground">
                    {insight.sub}
                </span>
            )}
            <span id={ctaId} hidden>
                {insight.cta_text}
            </span>
        </button>
    );
};

/**
 * Inline insight surface for brand and cat5 pages.
 *
 * Renders 3-4 deep-dive insight cards generated by AI Stories. Each card
 * surfaces a notable example (top related brand / top product format /
 * top ingredient / etc.) ranked by absolute YoY increase. Clicking a card
 * opens the AI Assistant panel and asks for the full list scoped to the
 * current entity — the user stays on the page, the agent surfaces the
 * ranked list inside the panel.
 */
const EntityInsightBar = ({
    entityId,
    entityType,
    entityName,
    entityRoute,
    country,
    dataSource = 'pop_index',
    week,
    description,
}) => {
    const headingId = useId();
    const cardHintId = useId();
    const { openPanel, sendMessage } = useAssistantContext();
    const { insights, isLoading, error } = useEntityInsight({
        entityId,
        entityType,
        entityRoute,
        country,
        dataSource,
        week,
    });

    const handleInsightClick = (insight) => {
        mixpanelTrack('Entity Insight Card Clicked', {
            entity_id: entityId,
            entity_type: entityType,
            entity_name: entityName,
            insight_id: insight.id,
            insight_label: insight.label,
        });
        // Start a fresh chat so the assistant has clean context — the prompt
        // refers to "the current page" and shouldn't be tangled with prior
        // conversation that might have shifted focus to a different entity.
        openPanel();
        sendMessage(insight.prompt, {
            newChat: true,
            source: 'entity_bar',
            is_suggestion: true,
            is_followup: true,
            follow_up_id: insight.id,
        });
    };

    if (error) {
        return (
            <div className="bg-muted rounded-lg px-4 py-3 flex items-start gap-3">
                <SpateAiIcon className="text-primary mt-0.5 flex-shrink-0" />
                <span className="text-sm text-foreground leading-relaxed">
                    Insights unavailable right now.
                </span>
            </div>
        );
    }

    return (
        <section aria-labelledby={headingId} className="flex flex-col gap-4">
            <div className="flex items-center gap-2 px-1">
                {isLoading ? (
                    <AnimatedSpateAiIcon className="text-primary shrink-0" />
                ) : (
                    <SpateAiIcon className="text-primary shrink-0" />
                )}
                <span
                    id={headingId}
                    className="text-sm font-semibold text-foreground whitespace-nowrap"
                >
                    AI Insights
                </span>
                {/* hidden, not sr-only: read only as each card's description */}
                <span id={cardHintId} hidden>
                    AI-generated. Opens in Spate AI:
                </span>
                <Popover>
                    <PopoverTrigger asChild>
                        <button
                            type="button"
                            aria-label="About AI Insights"
                            className="text-muted-foreground hover:text-foreground transition-colors"
                        >
                            <HelpCircle size={14} />
                        </button>
                    </PopoverTrigger>
                    <PopoverContent
                        side="top"
                        align="start"
                        sideOffset={8}
                        className="w-80 rounded-md border-0 bg-black px-4 py-3 text-sm leading-relaxed text-[#BCBCBC] shadow-[0_0_4px_6px_rgba(0,0,0,0.1)]"
                    >
                        We look at the{' '}
                        {entityType === 'category'
                            ? 'categories and brands'
                            : 'categories'}{' '}
                        consumers are viewing alongside{' '}
                        <span className="font-semibold text-white">
                            {entityName || 'the current page'}
                        </span>
                        , ranked by year-over-year increase. After filtering out
                        noise, we surface a few standouts worth a closer look.
                        The card text is written by AI. Click any card to dig
                        deeper in the AI Assistant.
                    </PopoverContent>
                </Popover>
            </div>

            {description}

            {(isLoading || insights.length > 0) && (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
                    {Array.from({ length: SKELETON_CARD_COUNT }).map((_, i) => {
                        const insight = insights[i];
                        if (insight) {
                            return (
                                <InsightCardErrorBoundary key={insight.id}>
                                    <InsightCard
                                        insight={insight}
                                        describedBy={cardHintId}
                                        onClick={() =>
                                            handleInsightClick(insight)
                                        }
                                    />
                                </InsightCardErrorBoundary>
                            );
                        }
                        return isLoading ? (
                            <InsightCardSkeleton key={`sk-${i}`} />
                        ) : null;
                    })}
                </div>
            )}
        </section>
    );
};

export default EntityInsightBar;
